/* eslint-disable prettier/prettier */
import "../../styles/ComplaintsPage.scss";
import { Badge, Button, Input, Label, Modal, ModalBody } from "reactstrap";
import {
  ComplaintFileUploadPayload,
  ComplaintRecord,
  ComplaintStatus,
  ComplaintStoredFile
} from "./types";
import {
  FirebaseReducer,
  FirestoreReducer,
  ReduxFirestoreQueries,
  firestoreConnect,
  isLoaded
} from "react-redux-firebase";
import { compose } from "redux";
import { connect } from "react-redux";
import { functions } from "../../config/firebase";
import { x } from "react-icons-kit/feather";
import Icon from "react-icons-kit";
import LoadingPage from "../loading/LoadingPage";
import React from "react";

interface ComplaintsPageProps {
  auth: FirebaseReducer.AuthState;
  profile: any;
  complaints: { [key: string]: ComplaintRecord };
  partners: { [key: string]: { name?: string } };
  loading: boolean;
}

type ComplaintTab = "submitted" | "received" | "all";

type ComplaintAction =
  | "comment"
  | "startReview"
  | "approve"
  | "reject"
  | "proposeBudget"
  | "approveBudget"
  | "contestBudget"
  | "resolve"
  | "escalate"
  | "markSolved";

const STATUS_OPTIONS: Array<"ALL" | ComplaintStatus> = [
  "ALL",
  "OPEN",
  "UNDER_REVIEW",
  "APPROVED",
  "REJECTED",
  "IN_PROGRESS",
  "RESOLVED",
  "ESCALATED"
];

const toDate = (value: any): Date | null => {
  if (!value) {
    return null;
  }
  try {
    if (typeof value?.toDate === "function") {
      return value.toDate() as Date;
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return null;
    }
    return date;
  } catch (error) {
    return null;
  }
};

const formatDate = (value: any): string => {
  const date = toDate(value);
  return date ? date.toLocaleDateString() : "-";
};

const statusClassName = (status: ComplaintStatus): string => {
  switch (status) {
    case "OPEN":
      return "complaint-status-pill open";
    case "UNDER_REVIEW":
      return "complaint-status-pill review";
    case "APPROVED":
      return "complaint-status-pill approved";
    case "REJECTED":
      return "complaint-status-pill rejected";
    case "IN_PROGRESS":
      return "complaint-status-pill progress";
    case "RESOLVED":
      return "complaint-status-pill resolved";
    case "ESCALATED":
      return "complaint-status-pill escalated";
    default:
      return "complaint-status-pill";
  }
};

const toUploadPayload = (file: File): Promise<ComplaintFileUploadPayload> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = typeof reader.result === "string" ? reader.result : "";
      const extension = file.name.includes(".")
        ? file.name.split(".").pop() || "dat"
        : "dat";
      resolve({
        file: result,
        ext: extension,
        name: file.name
      });
    };
    reader.onerror = () =>
      reject(reader.error || new Error("Unable to read file"));
    reader.readAsDataURL(file);
  });
};

const renderAttachmentList = (
  title: string,
  files: ComplaintStoredFile[] = []
) => {
  if (!files.length) {
    return null;
  }

  return (
    <div className="attachment-block">
      <h4>{title}</h4>
      <ul>
        {files.map((file) => (
          <li key={`${title}-${file.fileName}`}>
            <a href={file.url} target="_blank" rel="noopener noreferrer">
              {file.originalName || file.fileName}
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
};

const ComplaintsPage: React.FC<any> = ({
  complaints,
  loading,
  partners,
  profile,
  history
}) => {
  const isAdmin = profile?.partnerId === "Admin";
  const [tab, setTab] = React.useState<ComplaintTab>("all");
  const [selectedComplaintId, setSelectedComplaintId] = React.useState<
    string | null
  >(null);
  const [isDetailOpen, setIsDetailOpen] = React.useState<boolean>(false);
  const [statusFilter, setStatusFilter] = React.useState<
    "ALL" | ComplaintStatus
  >("ALL");
  const [locationFilter, setLocationFilter] = React.useState<string>("");
  const [memberFilter, setMemberFilter] = React.useState<string>("");
  const [serviceTypeFilter, setServiceTypeFilter] = React.useState<string>("");
  const [dateFrom, setDateFrom] = React.useState<string>("");
  const [dateTo, setDateTo] = React.useState<string>("");
  const [actionComment, setActionComment] = React.useState<string>("");
  const [budgetAmount, setBudgetAmount] = React.useState<string>("");
  const [budgetFeedback, setBudgetFeedback] = React.useState<string>("");
  const [resolutionNote, setResolutionNote] = React.useState<string>("");
  const [resolutionFiles, setResolutionFiles] = React.useState<File[]>([]);
  const [invoiceFiles, setInvoiceFiles] = React.useState<File[]>([]);
  const [proofOfPaymentFiles, setProofOfPaymentFiles] = React.useState<File[]>(
    []
  );
  const [submitting, setSubmitting] = React.useState<boolean>(false);
  const [feedbackMessage, setFeedbackMessage] = React.useState<string>("");
  const [feedbackError, setFeedbackError] = React.useState<string>("");

  const resolvePartyName = React.useCallback(
    (party?: { name?: string; partnerId?: string }) => {
      return (
        party?.name ||
        partners?.[party?.partnerId || ""]?.name ||
        party?.partnerId ||
        "-"
      );
    },
    [partners]
  );

  const complaintList = React.useMemo(() => {
    return Object.keys(complaints || {}).map((id: string) => ({
      ...(complaints[id] as ComplaintRecord),
      id
    }));
  }, [complaints]);

  const memberOptions = React.useMemo(() => {
    const values = new Map<string, string>();

    complaintList.forEach((complaint) => {
      [
        resolvePartyName(complaint.complainant),
        resolvePartyName(complaint.provider),
        complaint.service?.originName || complaint.service?.originPartnerId
      ].forEach((name) => {
        const trimmedName = String(name || "").trim();
        if (!trimmedName || trimmedName === "-") {
          return;
        }
        const key = trimmedName.toLowerCase();
        if (!values.has(key)) {
          values.set(key, trimmedName);
        }
      });
    });

    return Array.from(values.values()).sort((left, right) =>
      left.localeCompare(right)
    );
  }, [complaintList, resolvePartyName]);

  const serviceTypeOptions = React.useMemo(() => {
    const values = new Map<string, string>();

    complaintList.forEach((complaint) => {
      const serviceType = String(complaint.service?.serviceType || "").trim();
      if (!serviceType) {
        return;
      }
      const key = serviceType.toLowerCase();
      if (!values.has(key)) {
        values.set(key, serviceType);
      }
    });

    return Array.from(values.values()).sort((left, right) =>
      left.localeCompare(right)
    );
  }, [complaintList]);

  const filteredComplaints = React.useMemo(() => {
    const normalizedLocation = locationFilter.trim().toLowerCase();
    const normalizedMember = memberFilter.trim().toLowerCase();
    const normalizedServiceType = serviceTypeFilter.trim().toLowerCase();

    return complaintList
      .filter((complaint) => {
        if (tab === "submitted") {
          return complaint.complainant?.partnerId === profile?.partnerId;
        }
        if (tab === "received") {
          return complaint.provider?.partnerId === profile?.partnerId;
        }
        return true;
      })
      .filter((complaint) => {
        if (statusFilter === "ALL") {
          return true;
        }
        return complaint.status === statusFilter;
      })
      .filter((complaint) => {
        if (!normalizedLocation) {
          return true;
        }
        return String(complaint.location || "")
          .toLowerCase()
          .includes(normalizedLocation);
      })
      .filter((complaint) => {
        if (!normalizedMember) {
          return true;
        }
        const providerName = resolvePartyName(complaint.provider).toLowerCase();
        const originName = String(
          complaint.service?.originName ||
            complaint.service?.originPartnerId ||
            ""
        ).toLowerCase();
        const complainantName = resolvePartyName(
          complaint.complainant
        ).toLowerCase();
        return (
          providerName === normalizedMember ||
          originName === normalizedMember ||
          complainantName === normalizedMember
        );
      })
      .filter((complaint) => {
        if (!normalizedServiceType) {
          return true;
        }
        return (
          String(complaint.service?.serviceType || "")
            .trim()
            .toLowerCase() === normalizedServiceType
        );
      })
      .filter((complaint) => {
        const serviceDate = toDate(
          complaint.service?.serviceDate || complaint._audit?.created?.timestamp
        );
        if (!serviceDate) {
          return true;
        }
        const fromDate = dateFrom ? new Date(dateFrom) : null;
        const toDateValue = dateTo ? new Date(dateTo) : null;
        if (fromDate && serviceDate < fromDate) {
          return false;
        }
        if (toDateValue && serviceDate > toDateValue) {
          return false;
        }
        return true;
      })
      .sort((left, right) => {
        const leftDate =
          toDate(
            left.lastActionAt || left._audit?.updated?.timestamp
          )?.getTime() || 0;
        const rightDate =
          toDate(
            right.lastActionAt || right._audit?.updated?.timestamp
          )?.getTime() || 0;
        return rightDate - leftDate;
      });
  }, [
    complaintList,
    dateFrom,
    dateTo,
    locationFilter,
    memberFilter,
    profile,
    resolvePartyName,
    serviceTypeFilter,
    statusFilter,
    tab
  ]);

  React.useEffect(() => {
    if (!filteredComplaints.length) {
      setSelectedComplaintId(null);
      setIsDetailOpen(false);
      return;
    }

    const selectedExists = filteredComplaints.some(
      (complaint) => complaint.id === selectedComplaintId
    );
    if (!selectedExists) {
      setSelectedComplaintId(filteredComplaints[0].id);
    }
  }, [filteredComplaints, selectedComplaintId]);

  const selectedComplaint =
    filteredComplaints.find(
      (complaint) => complaint.id === selectedComplaintId
    ) || null;

  const openComplaintDetail = (complaintId: string) => {
    setSelectedComplaintId(complaintId);
    setIsDetailOpen(true);
    setFeedbackError("");
    setFeedbackMessage("");
  };

  const closeComplaintDetail = () => {
    setIsDetailOpen(false);
  };
  const isComplainant =
    selectedComplaint?.complainant?.partnerId === profile?.partnerId;
  const isProvider =
    selectedComplaint?.provider?.partnerId === profile?.partnerId;

  const resetActionForm = () => {
    setActionComment("");
    setBudgetAmount("");
    setBudgetFeedback("");
    setResolutionNote("");
    setResolutionFiles([]);
    setInvoiceFiles([]);
    setProofOfPaymentFiles([]);
  };

  const submitAction = async (action: ComplaintAction) => {
    if (!selectedComplaint) {
      return;
    }

    setSubmitting(true);
    setFeedbackError("");
    setFeedbackMessage("");

    try {
      const payload: any = {
        id: selectedComplaint.id,
        action,
        comment: actionComment || null,
        budgetFeedback: budgetFeedback || null,
        resolutionNote: resolutionNote || null
      };

      if (action === "proposeBudget") {
        payload.budgetAmount = Number(budgetAmount);
      }

      if (action === "resolve") {
        const [resolutionPayloads, invoicePayloads, proofPayloads] =
          await Promise.all([
            Promise.all(resolutionFiles.map((file) => toUploadPayload(file))),
            Promise.all(invoiceFiles.map((file) => toUploadPayload(file))),
            Promise.all(
              proofOfPaymentFiles.map((file) => toUploadPayload(file))
            )
          ]);
        payload.resolutionFiles = resolutionPayloads;
        payload.invoiceFiles = invoicePayloads;
        payload.proofOfPaymentFiles = proofPayloads;
      }

      const callable = functions.httpsCallable("updateComplaintWorkflow");
      await callable({
        ...payload,
        partnerId: profile?.partnerId || null
      });
      setFeedbackMessage("Complaint workflow updated.");
      resetActionForm();
    } catch (error) {
      console.error(error);
      setFeedbackError("The complaint could not be updated.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <LoadingPage />;
  }

  const statusCounts = complaintList.reduce(
    (
      accumulator: { open: number; review: number; resolved: number },
      complaint
    ) => {
      if (complaint.status === "OPEN") {
        accumulator.open += 1;
      }
      if (
        complaint.status === "UNDER_REVIEW" ||
        complaint.status === "APPROVED" ||
        complaint.status === "IN_PROGRESS"
      ) {
        accumulator.review += 1;
      }
      if (complaint.status === "RESOLVED") {
        accumulator.resolved += 1;
      }
      return accumulator;
    },
    { open: 0, review: 0, resolved: 0 }
  );

  return (
    <div className="padded-container complaints-page-container">
      <div className="complaints-header">
        <div>
          <h1>Complaints Management</h1>
          <span className="title-underline-v2" />
          <p>
            Track complaints submitted, received, and resolved across your
            member dashboard.
          </p>
        </div>
        <Button className="new-compaint-button" color="primary" onClick={() => history.push("/complaints/new")}>
          New Complaint
        </Button>
      </div>

      <div className="complaint-summary-strip">
        <div className="complaint-summary-card">
          <strong>{complaintList.length}</strong>
          <span>Total complaints</span>
        </div>
        <div className="complaint-summary-card">
          <strong>{statusCounts.open}</strong>
          <span>Open</span>
        </div>
        <div className="complaint-summary-card">
          <strong>{statusCounts.review}</strong>
          <span>Under review / in progress</span>
        </div>
        <div className="complaint-summary-card">
          <strong>{statusCounts.resolved}</strong>
          <span>Resolved</span>
        </div>
      </div>

      <div className="complaints-layout complaints-layout--queue">
        <section className="complaint-card complaints-list-card complaints-list-card--full">
          <div className="complaints-toolbar">
            <div className="complaint-tabs">
              <button
                type="button"
                className={tab === "submitted" ? "active" : ""}
                onClick={() => setTab("submitted")}
              >
                Complaints Submitted
              </button>
              <button
                type="button"
                className={tab === "received" ? "active" : ""}
                onClick={() => setTab("received")}
              >
                Complaints Received
              </button>
              <button
                type="button"
                className={tab === "all" ? "active" : ""}
                onClick={() => setTab("all")}
              >
                All Complaints
              </button>
            </div>

            <div className="complaint-filter-grid">
              <Input
                type="select"
                value={statusFilter}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  setStatusFilter(event.target.value as "ALL" | ComplaintStatus)
                }
              >
                {STATUS_OPTIONS.map((status) => (
                  <option key={status} value={status}>
                    {status === "ALL" ? "All Statuses" : status}
                  </option>
                ))}
              </Input>
              <Input
                value={locationFilter}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  setLocationFilter(event.target.value)
                }
                placeholder="Location"
              />
              <Input
                type="select"
                value={memberFilter}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  setMemberFilter(event.target.value)
                }
              >
                <option value="">All Members</option>
                {memberOptions.map((member) => (
                  <option key={member} value={member}>
                    {member}
                  </option>
                ))}
              </Input>
              <Input
                type="select"
                value={serviceTypeFilter}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  setServiceTypeFilter(event.target.value)
                }
              >
                <option value="">All Service Types</option>
                {serviceTypeOptions.map((serviceType) => (
                  <option key={serviceType} value={serviceType}>
                    {serviceType}
                  </option>
                ))}
              </Input>
              <Input
                type="date"
                value={dateFrom}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  setDateFrom(event.target.value)
                }
              />
              <Input
                type="date"
                value={dateTo}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  setDateTo(event.target.value)
                }
              />
            </div>
          </div>

          <div className="complaint-list-scroll">
            {filteredComplaints.length ? (
              filteredComplaints.map((complaint) => {
                const providerName = resolvePartyName(complaint.provider);
                return (
                  <button
                    type="button"
                    key={complaint.id}
                    className={`complaint-list-card ${
                      selectedComplaint?.id === complaint.id ? "active" : ""
                    }`}
                    onClick={() => openComplaintDetail(complaint.id)}
                  >
                    <div className="complaint-list-card__top">
                      <strong>
                        {complaint.service?.claimNumber || complaint.id}
                      </strong>
                      <span className={statusClassName(complaint.status)}>
                        {complaint.status}
                      </span>
                    </div>
                    <div className="complaint-list-card__meta">
                      <span>Motive: {complaint.motive}</span>
                      <span>
                        Origin:{" "}
                        {complaint.service?.originName ||
                          complaint.service?.originPartnerId ||
                          "-"}
                      </span>
                      <span>Provider: {providerName}</span>
                      <span>
                        Client: {complaint.service?.clientName || "-"}
                      </span>
                      <span>Location: {complaint.location || "-"}</span>
                      <span>
                        Service Date: {formatDate(complaint.service?.serviceDate)}
                      </span>
                    </div>
                    <div className="complaint-list-card__footer">
                      <span className="complaint-open-link">Open Details</span>
                    </div>
                  </button>
                );
              })
            ) : (
              <div className="empty-complaint-state">
                No complaints match the selected filters.
              </div>
            )}
          </div>
        </section>
      </div>

      <Modal
        isOpen={Boolean(selectedComplaint) && isDetailOpen}
        toggle={closeComplaintDetail}
        size="xl"
        className="complaint-detail-modal"
        contentClassName="complaint-detail-modal__content"
      >
        {!selectedComplaint ? null : (
          <>
            <div className="complaint-detail-header">
              <div>
                <h2>
                  {selectedComplaint.service?.claimNumber || selectedComplaint.id}
                </h2>
                <p>{selectedComplaint.motive}</p>
              </div>
              <div className="complaint-detail-header-actions">
                <Badge className={statusClassName(selectedComplaint.status)}>
                  {selectedComplaint.status}
                </Badge>
                <button
                  type="button"
                  className="complaint-close-btn"
                  onClick={closeComplaintDetail}
                >
                  <Icon icon={x} size={22} />
                </button>
              </div>
            </div>

            <ModalBody className="complaint-detail-body">
              <div className="complaint-detail-layout">
                <div className="complaint-detail-main">
                  <div className="complaint-detail-grid">
                    <div>
                      <Label>Complainant</Label>
                      <p>{selectedComplaint.complainant?.name || "-"}</p>
                    </div>
                    <div>
                      <Label>Origin Member</Label>
                      <p>
                        {selectedComplaint.service?.originName ||
                          selectedComplaint.service?.originPartnerId ||
                          "-"}
                      </p>
                    </div>
                    <div>
                      <Label>Service Provider</Label>
                      <p>{selectedComplaint.provider?.name || "-"}</p>
                    </div>
                    <div>
                      <Label>Location</Label>
                      <p>{selectedComplaint.location || "-"}</p>
                    </div>
                    <div>
                      <Label>Service type</Label>
                      <p>{selectedComplaint.service?.serviceType || "-"}</p>
                    </div>
                    <div>
                      <Label>Service date</Label>
                      <p>{formatDate(selectedComplaint.service?.serviceDate)}</p>
                    </div>
                    <div>
                      <Label>Guarantee code</Label>
                      <p>{selectedComplaint.service?.guaranteeCode || "-"}</p>
                    </div>
                  </div>

                  <div className="complaint-description-block">
                    <h3>Description</h3>
                    <p>
                      {selectedComplaint.description ||
                        "No additional description was added."}
                    </p>
                  </div>

                  <div className="complaint-attachments-grid">
                    {renderAttachmentList(
                      "Complaint media",
                      selectedComplaint.mediaFiles || []
                    )}
                    {renderAttachmentList(
                      "Guarantee PDF",
                      selectedComplaint.guaranteeFiles || []
                    )}
                    {renderAttachmentList(
                      "Fixed service photos",
                      selectedComplaint.resolution?.fixedServiceFiles || []
                    )}
                    {renderAttachmentList(
                      "Invoice",
                      selectedComplaint.resolution?.invoiceFiles || []
                    )}
                    {renderAttachmentList(
                      "Proof of payment",
                      selectedComplaint.resolution?.proofOfPaymentFiles || []
                    )}
                  </div>
                </div>

                <div className="complaint-detail-sidebar">
                  <div className="complaint-budget-block">
                    <h3>Budget</h3>
                    <p>
                      Amount:{" "}
                      <strong>
                        {selectedComplaint.budget?.amount
                          ? `€${selectedComplaint.budget.amount}`
                          : "-"}
                      </strong>
                    </p>
                    <p>
                      Status:{" "}
                      <strong>{selectedComplaint.budget?.status || "-"}</strong>
                    </p>
                    {selectedComplaint.budget?.feedback ? (
                      <p>Feedback: {selectedComplaint.budget.feedback}</p>
                    ) : null}
                    {selectedComplaint.providerResponse ? (
                      <p>Provider response: {selectedComplaint.providerResponse}</p>
                    ) : null}
                  </div>

                  <div className="complaint-timeline-block">
                    <h3>Timeline</h3>
                    <div className="complaint-timeline-list">
                      {(selectedComplaint.timeline || [])
                        .slice()
                        .reverse()
                        .map((entry, index) => (
                          <div
                            key={`${entry.action}-${index}`}
                            className="timeline-item"
                          >
                            <strong>{entry.actor?.name || "Member"}</strong>
                            <span>{entry.action}</span>
                            <small>{formatDate(entry.createdAt)}</small>
                            {entry.comment ? <p>{entry.comment}</p> : null}
                          </div>
                        ))}
                    </div>
                  </div>

                  <div className="complaint-action-card">
                    <h3>Workflow Actions</h3>
                    <Input
                      type="textarea"
                      rows={3}
                      value={actionComment}
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                        setActionComment(event.target.value)
                      }
                      placeholder="Add a comment or workflow note"
                    />

                    <div className="complaint-action-row">
                      <Button
                        outline
                        onClick={() => submitAction("comment")}
                        disabled={submitting}
                      >
                        Add Comment
                      </Button>
                      {(isProvider || isAdmin) &&
                      selectedComplaint.status === "OPEN" ? (
                        <Button
                          color="secondary"
                          onClick={() => submitAction("startReview")}
                          disabled={submitting}
                        >
                          Mark Under Review
                        </Button>
                      ) : null}
                      {(isProvider || isAdmin) &&
                      (selectedComplaint.status === "OPEN" ||
                        selectedComplaint.status === "UNDER_REVIEW") ? (
                        <>
                          <Button
                            color="success"
                            onClick={() => submitAction("approve")}
                            disabled={submitting}
                          >
                            Approve Complaint
                          </Button>
                          <Button
                            color="danger"
                            onClick={() => submitAction("reject")}
                            disabled={submitting}
                          >
                            Reject Complaint
                          </Button>
                        </>
                      ) : null}
                    </div>

                    {(isComplainant || isAdmin) &&
                    selectedComplaint.status === "APPROVED" ? (
                      <div className="complaint-inline-form">
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          value={budgetAmount}
                          onChange={(
                            event: React.ChangeEvent<HTMLInputElement>
                          ) => setBudgetAmount(event.target.value)}
                          placeholder="Budget amount"
                        />
                        <Input
                          value={budgetFeedback}
                          onChange={(
                            event: React.ChangeEvent<HTMLInputElement>
                          ) => setBudgetFeedback(event.target.value)}
                          placeholder="Budget note or explanation"
                        />
                        <Button
                          color="primary"
                          onClick={() => submitAction("proposeBudget")}
                          disabled={submitting || !budgetAmount}
                        >
                          Submit Budget
                        </Button>
                      </div>
                    ) : null}

                    {(isProvider || isAdmin) &&
                    selectedComplaint.status === "APPROVED" &&
                    selectedComplaint.budget?.status === "PENDING" ? (
                      <div className="complaint-inline-form">
                        <Input
                          value={budgetFeedback}
                          onChange={(
                            event: React.ChangeEvent<HTMLInputElement>
                          ) => setBudgetFeedback(event.target.value)}
                          placeholder="Optional feedback"
                        />
                        <Button
                          color="success"
                          onClick={() => submitAction("approveBudget")}
                          disabled={submitting}
                        >
                          Approve Budget
                        </Button>
                        <Button
                          color="warning"
                          onClick={() => submitAction("contestBudget")}
                          disabled={submitting}
                        >
                          Contest Budget
                        </Button>
                      </div>
                    ) : null}

                    {(isComplainant || isAdmin) &&
                    selectedComplaint.status === "IN_PROGRESS" ? (
                      <div className="complaint-resolution-form">
                        <Input
                          type="textarea"
                          rows={3}
                          value={resolutionNote}
                          onChange={(
                            event: React.ChangeEvent<HTMLInputElement>
                          ) => setResolutionNote(event.target.value)}
                          placeholder="Final resolution note"
                        />
                        <div className="complaint-upload-grid">
                          <div>
                            <Label>Completed service photos</Label>
                            <Input
                              type="file"
                              multiple
                              accept="image/*,video/*"
                              onChange={(
                                event: React.ChangeEvent<HTMLInputElement>
                              ) => {
                                setResolutionFiles(
                                  Array.from(event.target.files || []) as File[]
                                );
                              }}
                            />
                          </div>
                          <div>
                            <Label>Invoice</Label>
                            <Input
                              type="file"
                              multiple
                              accept="image/*,application/pdf"
                              onChange={(
                                event: React.ChangeEvent<HTMLInputElement>
                              ) => {
                                setInvoiceFiles(
                                  Array.from(event.target.files || []) as File[]
                                );
                              }}
                            />
                          </div>
                          <div>
                            <Label>Proof of payment</Label>
                            <Input
                              type="file"
                              multiple
                              accept="image/*,application/pdf"
                              onChange={(
                                event: React.ChangeEvent<HTMLInputElement>
                              ) => {
                                setProofOfPaymentFiles(
                                  Array.from(event.target.files || []) as File[]
                                );
                              }}
                            />
                          </div>
                        </div>
                        <Button
                          color="success"
                          onClick={() => submitAction("resolve")}
                          disabled={submitting}
                        >
                          Mark Resolved
                        </Button>
                      </div>
                    ) : null}

                    {(isComplainant || isAdmin) &&
                    selectedComplaint.status === "REJECTED" ? (
                      <div className="complaint-action-row">
                        <Button
                          color="warning"
                          onClick={() => submitAction("escalate")}
                          disabled={submitting}
                        >
                          Escalate to AGE Technical Team
                        </Button>
                        <Button
                          color="secondary"
                          onClick={() => submitAction("markSolved")}
                          disabled={submitting}
                        >
                          Mark as Solved
                        </Button>
                      </div>
                    ) : null}

                    {feedbackError ? (
                      <div className="complaint-feedback error">
                        {feedbackError}
                      </div>
                    ) : null}
                    {feedbackMessage ? (
                      <div className="complaint-feedback success">
                        {feedbackMessage}
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>
            </ModalBody>
          </>
        )}
      </Modal>
    </div>
  );
};

const preMapStateToProps = ({
  firebase: { profile }
}: {
  firebase: FirebaseReducer.Reducer;
}) => ({
  profile
});

const mapQueryToProps = ({
  profile
}: {
  profile: any;
}): ReduxFirestoreQueries => {
  if (!profile || !profile.isLoaded || profile.isEmpty) {
    return [];
  }

  if (profile.partnerId === "Admin") {
    return [
      {
        collection: "complaints",
        storeAs: "complaints"
      },
      {
        collection: "partners",
        storeAs: "partners"
      }
    ];
  }

  return [
    {
      collection: "complaints",
      where: ["participantPartnerIds", "array-contains", profile.partnerId],
      storeAs: "complaints"
    },
    {
      collection: "partners",
      storeAs: "partners"
    }
  ];
};

const mapStateToProps = ({
  firestore,
  firebase: { auth, profile }
}: {
  firestore: FirestoreReducer.Reducer;
  firebase: FirebaseReducer.Reducer;
}) => {
  const complaintData = (firestore.data.complaints || {}) as {
    [key: string]: ComplaintRecord;
  };
  const partners = (firestore.data.partners || {}) as {
    [key: string]: { name?: string };
  };
  const loading =
    !isLoaded(firestore.data.complaints) || !isLoaded(firestore.data.partners);

  return {
    auth,
    profile,
    complaints: complaintData,
    partners,
    loading
  };
};

export default compose(
  connect(preMapStateToProps),
  firestoreConnect(mapQueryToProps),
  connect(mapStateToProps)
)(ComplaintsPage);
